import { getReviewStatusMeaning } from '../../utils/assessment.utils';
import { getAssessmentStatusMeaning } from '../../utils/assessment.utils';
import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  input,
  output,
  model,
  signal,
  computed,
  inject,
  effect,
} from '@angular/core';
import { map } from 'rxjs';
import { DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AssessmentRecord,
  AssessmentResponse,
  AssessmentStatusEnum,
  ContextDocument,
} from '../../models';
import type { DpiaRiskProfileData } from '../../models/assessments/dpia.model';
import { getPlugin } from '../../services/assessments/assessment-type.registry';
import { EntityLinkService } from '../../services/entity-link/entity-link';
import { DpiaDrawerReportComponent } from '../assessments/dpia/drawer-report/drawer-report';
import { VendorApi } from '../../services/vendor-api/vendor-api';
import { DetailPanelComponent } from '../detail-panel/detail-panel';
import { EditablePanelTitleComponent } from '../editable-panel-title/editable-panel-title';
import { PanelTabsComponent, PanelTab } from '../panel-tabs/panel-tabs';
import { TypeIconComponent } from '../type-icon/type-icon';
import { EmptyStateComponent } from '../empty-state/empty-state';
import { LabelNamesPipe } from '../../shared/pipes/label-names.pipe';
import { SkeletonDirective } from '../../shared/directives';
import { AssessmentReviewStatus } from '../../constants/assessment.constants';
import { ActionGlyphComponent } from '../action-glyph/action-glyph';
import { ModelCardSummaryComponent } from '../model-card-summary/model-card-summary';
import {
  getAssessmentStatusLabel,
  getAssessmentStatusVariant,
  getAssessmentIconConfig,
  getAssessmentDisplayOrgName,
  getReviewStatusLabel,
  canReplaceRecordOnlyTia,
  isRecordOnlyTia,
  isRetiredRecordOnlyTia,
} from '../../utils/assessment.utils';
import { TiaRecordSummaryComponent } from '../assessments/tia/record-summary/tia-record-summary';
import { ButtonComponent } from '../button/button';
import { getReviewStatusVariant } from '../../utils/status-indicator.utils';
import { StatusIndicatorComponent } from '../status-indicator/status-indicator';
import {
  formatFileSize,
  getContextDocFileTypeLabel,
  getContextDocIconConfig,
} from '../../utils/document.utils';
import { ContextDocPreviewComponent } from '../context-doc-preview/context-doc-preview';
import { createContextDocPreviewHandler } from '../../shared/context-doc-preview-handler';
import { getInitials, getAvatarColor } from '../../utils/display.utils';
import { getUserFriendlyErrorMessage } from '../../utils/error.utils';
import { StateStoreService } from '../../services/state-store/state-store';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import { getAssessmentExportPermission } from '../../shared/export-permissions';
import { PanelTaskListComponent } from '../panel-task-list/panel-task-list';
import { TrustCenterVisibilityToggleComponent } from '../trust-center-visibility-toggle/trust-center-visibility-toggle';
import { LanguageBadgeComponent } from '../language-badge/language-badge';
import { AuditTimelineComponent } from '../audit-timeline/audit-timeline';
import { createAuditTrailLoader } from '../../shared/audit-trail-loader';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-assessment-info-panel',
  imports: [
    DatePipe,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatMenuModule,
    TranslocoModule,
    ActionGlyphComponent,
    StatusIndicatorComponent,
    DetailPanelComponent,
    EditablePanelTitleComponent,
    PanelTabsComponent,
    TypeIconComponent,
    EmptyStateComponent,
    LabelNamesPipe,
    PanelTaskListComponent,
    ContextDocPreviewComponent,
    SkeletonDirective,
    ModelCardSummaryComponent,
    TrustCenterVisibilityToggleComponent,
    LanguageBadgeComponent,
    DpiaDrawerReportComponent,
    AuditTimelineComponent,
    TiaRecordSummaryComponent,
    ButtonComponent,
  ],
  templateUrl: './assessment-info-panel.html',
  styleUrl: './assessment-info-panel.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssessmentInfoPanelComponent {
  readonly getReviewStatusMeaning = getReviewStatusMeaning;

  readonly getAssessmentStatusMeaning = getAssessmentStatusMeaning;

  // --- Inputs ---
  isOpen = input(false);
  assessment = input<AssessmentRecord | null>(null);
  isExporting = input(false);
  displayOrgName = input<string | null>(null);

  // --- Outputs ---
  panelClose = output<void>();
  openInNewTab = output<void>();
  edit = output<AssessmentRecord>();
  export = output<AssessmentRecord>();
  share = output<AssessmentRecord>();
  delete = output<AssessmentRecord>();
  assessmentUpdated = output<AssessmentRecord>();
  requestReview = output<AssessmentRecord>();
  approve = output<AssessmentRecord>();
  /** VER-377 — "Start a full TIA instead" on a record-only TIA. */
  startFullTia = output<AssessmentRecord>();

  // --- Injected services ---
  private readonly stateService = inject(StateStoreService);
  private readonly transloco = inject(TranslocoService);
  private readonly translationVersion = signal(0);
  private readonly orgManagementApi = inject(OrgManagementApi);
  private readonly vendorApi = inject(VendorApi);
  private readonly entityLink = inject(EntityLinkService);
  private readonly destroyRef = inject(DestroyRef);

  // --- State ---
  readonly isManager = computed(() => this.stateService.isManager());
  readonly isSuperManager = computed(() => this.stateService.isSuperManager());

  readonly canRequestReview = computed(() => !this.isManager() && !this.isSuperManager());

  /**
   * May the caller verify *this* assessment straight from the drawer, without
   * waiting for a customer-initiated review request?
   *
   * T13 moved this off `isSuperManager()` and onto `assessment_review` held
   * on the assessment's own org. Two consequences worth stating:
   *
   *  * A SUPER_MANAGER member who was **not** granted review on this customer
   *    no longer sees the affordance. That is the tightening — the old check
   *    was target-blind, so it answered the same for every org.
   *  * Write access is no longer sufficient. `assessment_write` neither
   *    implies review nor is implied by it, so someone who edits this
   *    assessment but cannot bless it is a normal state, not a bug.
   *
   * `userHasSuperManagerAuthority` remains the legacy fallback inside
   * `canVerifyAssessmentsIn`, so an org this epic never configured behaves
   * exactly as before.
   */
  readonly canApproveDirectly = computed(() => {
    const a = this.assessment();
    if (!a) return false;
    if (!this.stateService.canVerifyAssessmentsIn(a.org_id)) return false;
    if (a.assessment_status !== AssessmentStatusEnum.COMPLETED) return false;
    return a.review_status !== AssessmentReviewStatus.REVIEWED;
  });

  /** Canonical export gate — see `shared/export-permissions.ts`. */
  readonly exportPermission = computed(() =>
    getAssessmentExportPermission(this.stateService, this.assessment())
  );
  readonly canExport = computed(() => this.exportPermission().allowed);
  readonly exportDisabledTooltip = computed(() => {
    this.translationVersion();
    const key = this.exportPermission().reasonKey;
    return key ? this.transloco.translate<string>(key) : '';
  });

  /** Trust Centre visibility eligibility — mirrors the panel filter at
   *  `trust-center.store.ts`: only COMPLETED assessments with a
   *  populated risk profile are publishable. */
  readonly isTrustCenterEligible = computed(() => {
    const a = this.assessment();
    if (!a) return false;
    return a.assessment_status === AssessmentStatusEnum.COMPLETED && a.risk_profile != null;
  });

  // Description expand/collapse (Overview tab)
  readonly descriptionExpanded = signal(false);
  readonly descriptionNeedsToggle = computed(
    () => (this.assessment()?.assessment_description ?? '').length > 240
  );

  // Panel width (two-way bound to detail-panel)
  width = model(500);

  // Tabs
  activeTab = signal<'overview' | 'risk' | 'tasks' | 'history'>('overview');

  // Audit trail — mirrors the pattern in document-detail-panel and
  // control-detail-panel. The BE emits `entity_type="assessment"` audit
  // events on every review-status change (crp-be assessment_handler);
  // the query handler in crp-audit-engine accepts any entity_type string,
  // so no BE change is required to consume this.
  readonly auditLoader = createAuditTrailLoader({
    getOrgId: () => this.assessment()?.org_id ?? null,
    entityType: 'assessment',
    getEntityId: () => this.assessment()?.assessment_id ?? '',
    api: (orgId, type, id) => this.orgManagementApi.getAuditTrail(orgId, type, id),
  });

  /** True when the loaded assessment renders through the DPIA panel
   *  tree. Same discriminator the full-page editor uses (via the plugin
   *  registry), so drawer + editor never disagree on shape. */
  readonly isDpia = computed(() => {
    const type = this.assessment()?.assessment_type;
    if (!type) return false;
    return getPlugin(type).resultRenderer === 'dpia_panels';
  });

  /** Type-narrowed access to the DPIA payload. Returns `null` for
   *  non-DPIA assessments or legacy rows missing the `kind` discriminator
   *  — callers gate on `isDpia()` before consuming DPIA-specific fields. */
  readonly dpiaProfile = computed<DpiaRiskProfileData | null>(() => {
    const rp = this.assessment()?.risk_profile;
    if (!rp) return null;
    return (rp as { kind?: string }).kind === 'dpia'
      ? (rp as unknown as DpiaRiskProfileData)
      : null;
  });

  private readonly _linkedActivityName = signal<string | null>(null);
  readonly linkedActivityName = this._linkedActivityName.asReadonly();

  private readonly _taskCount = signal(0);

  /** Middle-tab label is type-specific: "Report" for DPIA (matches the
   *  full-page editor terminology), "Model card" for AI Risk. Overview
   *  and Tasks are universal. Kept as a single computed so the label
   *  updates atomically with the type. */
  panelTabs = computed<PanelTab[]>(() => {
    this.translationVersion();
    const middleKey = this.isDpia()
      ? 'panel_assessment_info.tab_report'
      : 'panel_assessment_info.tab_model_card';
    return [
      {
        id: 'overview',
        label: this.transloco.translate<string>('panel_assessment_info.tab_overview'),
      },
      { id: 'risk', label: this.transloco.translate<string>(middleKey) },
      {
        id: 'tasks',
        label: this.transloco.translate<string>('panel_assessment_info.tab_tasks'),
        count: this._taskCount() || undefined,
      },
      {
        id: 'history',
        label: this.transloco.translate<string>('panel_assessment_info.tab_history'),
      },
    ];
  });

  /** One-line screening summary rendered in the DPIA sub-hero. Reads
   *  the profile first (post-generation state), falls back to the
   *  denormalised pa_link snapshot (pre-generation). Null when neither
   *  side has an outcome — the sub-hero row hides. */
  readonly screeningSummary = computed<{ labelKey: string; criteriaCount: number } | null>(() => {
    const a = this.assessment();
    if (!a) return null;
    const outcome = this.dpiaProfile()?.screening_outcome ?? a.pa_link?.screening_outcome ?? null;
    if (!outcome) return null;
    const criteria =
      this.dpiaProfile()?.screening_rationale?.length ??
      a.pa_link?.screening_rationale?.length ??
      0;
    return {
      labelKey: `dpia.screening.outcome_${outcome.toLowerCase()}`,
      criteriaCount: criteria,
    };
  });

  /** True when the DPIA sub-hero row has anything worth rendering
   *  (linked PA or a screening outcome). Non-DPIA assessments always
   *  short-circuit false via `isDpia()`. */
  readonly hasDpiaSubHero = computed(
    () => this.isDpia() && (!!this.assessment()?.pa_link || !!this.screeningSummary())
  );

  // --- Context documents ---
  private readonly _resolvedContextDocs = signal<ContextDocument[]>([]);
  /** True while the constructor effect is fetching context-doc records.
   *  Templates use it to swap the real row markup for shimmer skeleton
   *  rows so the section doesn't pop in once the fetch lands. */
  private readonly _isLoadingContextDocs = signal(false);
  readonly isLoadingContextDocs = this._isLoadingContextDocs.asReadonly();
  private readonly _contextDocsError = signal<string | null>(null);
  readonly contextDocsError = this._contextDocsError.asReadonly();
  private readonly _contextDocsRetry = signal(0);
  private _contextDocsOwnerKey: string | null = null;

  readonly usedContextDocs = computed(() => {
    const ids = new Set(this.assessment()?.context_document_ids ?? []);
    if (!ids.size) return [];
    const provided = this._resolvedContextDocs();
    return provided.filter((d: ContextDocument) => ids.has(d.context_document_id));
  });

  /** Post-load empty state: assessment claims it has context docs but the
   *  resolve fetch returned nothing matching. Surfaces a "(none available)"
   *  message instead of leaving the section visually empty under the count
   *  badge — typical causes are docs deleted after the assessment ran or a
   *  cross-org access gap when the fetch happens to succeed-with-empty. */
  readonly contextDocsEmptyAfterLoad = computed(() => {
    if (this.isLoadingContextDocs() || this.contextDocsError()) return false;
    const ids = this.assessment()?.context_document_ids ?? [];
    return ids.length > 0 && this.usedContextDocs().length === 0;
  });

  /** Count rendered next to the section label. While the resolve fetch is
   *  in flight we optimistically show the expected count from the
   *  assessment's `context_document_ids` (skeleton rows are visible). Once
   *  the fetch lands we switch to the actually-resolvable count so a
   *  stale-FK case (1 id → 0 resolved docs) reads as 0 instead of
   *  misreporting "1" above an empty-state message. Templates hide the
   *  badge when this is 0 so the layout stays clean. */
  readonly displayedContextDocCount = computed(() => {
    if (this.isLoadingContextDocs()) {
      return this.assessment()?.context_document_ids?.length ?? 0;
    }
    return this.usedContextDocs().length;
  });

  readonly getContextDocIconConfig = getContextDocIconConfig;
  readonly getContextDocFileTypeLabel = getContextDocFileTypeLabel;
  readonly formatFileSize = formatFileSize;

  /** The org whose context docs to query — the assessment's own org.
   *  `context_document_handler.handle_get` is strictly path-scoped, so
   *  this is passed directly as the path; there's no separate
   *  caller-vs-customer split any more (dropped along with
   *  `customer_org_id` to close a cross-customer data leak, see commit
   *  9993c021). Returns null while the assessment hasn't resolved yet. */
  private contextDocScope(): { customerOrgId: string } | null {
    const customerOrgId = this.assessment()?.org_id;
    return customerOrgId ? { customerOrgId } : null;
  }

  retryContextDocs(): void {
    this._contextDocsRetry.update(value => value + 1);
  }

  /** Shared preview state — same handler edit-assessment uses (single
   *  source of truth for the open/close/retry triple). Refetch hits
   *  the same `document_ids`-filtered list endpoint the constructor's
   *  effect uses, so the retry path mints fresh presigned URLs and the
   *  resolved-doc cache stays in sync. */
  readonly contextDocPreview = createContextDocPreviewHandler({
    destroyRef: this.destroyRef,
    refetch: previewDoc => {
      const assessment = this.assessment();
      const ids = previewDoc
        ? [previewDoc.context_document_id]
        : (assessment?.context_document_ids ?? []);
      const scope = this.contextDocScope();
      if (!scope || !ids.length) return null;
      return this.orgManagementApi
        .getContextDocuments(scope.customerOrgId, {
          document_ids: ids,
          source_owner_kind: 'assessment',
          source_owner_id: assessment!.assessment_id,
        })
        .pipe(
          map(resp => {
            this._resolvedContextDocs.set(resp.context_documents);
            return resp.context_documents;
          })
        );
    },
  });

  constructor() {
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.translationVersion.update(v => v + 1));

    // Collapse description + reload audit trail only when the panel
    // switches to a different assessment. Pinning the dep to a
    // computed of the id string dedupes via Object.is — otherwise
    // this fires on every 5 s poll tick (same row, new reference).
    const loadedAssessmentId = computed(() => this.assessment()?.assessment_id ?? null);
    effect(() => {
      const id = loadedAssessmentId();
      if (!id) return;
      this.descriptionExpanded.set(false);
      this.auditLoader.load();
    });

    this.destroyRef.onDestroy(() => this.auditLoader.destroy());

    // Resolve the linked PA's display name for the DPIA overview chip.
    // Mirrors the edit-assessment effect: pa_link carries `activity_id`
    // but not the name, so we fetch it on demand. Non-DPIA assessments
    // (no pa_link) short-circuit to null. Race-guarded on activity_id so
    // a fast switch between two linked DPIAs never resolves the stale
    // fetch. Errors silently null the name — the chip falls back to a
    // generic "Linked activity" label.
    effect((onCleanup: (fn: () => void) => void) => {
      const a = this.assessment();
      const orgId = a?.org_id;
      const activityId = a?.pa_link?.activity_id;
      if (!orgId || !activityId) {
        this._linkedActivityName.set(null);
        return;
      }
      const sub = this.vendorApi.getProcessingActivity(orgId, activityId).subscribe({
        next: pa => {
          if (this.assessment()?.pa_link?.activity_id === activityId) {
            this._linkedActivityName.set(pa.name?.trim() || null);
          }
        },
        error: () => {
          if (this.assessment()?.pa_link?.activity_id === activityId) {
            this._linkedActivityName.set(null);
          }
        },
      });
      onCleanup(() => sub.unsubscribe());
    });

    effect((onCleanup: (fn: () => void) => void) => {
      const a = this.assessment();
      this._contextDocsRetry();
      const ownerKey = a ? `${a.org_id}:${a.assessment_id}` : null;
      if (this._contextDocsOwnerKey !== ownerKey) {
        this._resolvedContextDocs.set([]);
        this._contextDocsOwnerKey = ownerKey;
      }
      const ids = a?.context_document_ids;
      if (!ids?.length) {
        this._resolvedContextDocs.set([]);
        this._isLoadingContextDocs.set(false);
        this._contextDocsError.set(null);
        return;
      }
      const scope = this.contextDocScope();
      if (!scope) {
        this._resolvedContextDocs.set([]);
        this._isLoadingContextDocs.set(false);
        this._contextDocsError.set(null);
        return;
      }
      this._isLoadingContextDocs.set(true);
      this._contextDocsError.set(null);

      // Resolve only within the assessment org and assessment owner scope.
      // A failed or empty customer request must never broaden the lookup to
      // the manager's org.
      const sub = this.orgManagementApi
        .getContextDocuments(scope.customerOrgId, {
          document_ids: ids,
          source_owner_kind: 'assessment',
          source_owner_id: a!.assessment_id,
        })
        .subscribe({
          next: resp => {
            const current = this.assessment();
            if (current?.assessment_id !== a!.assessment_id || current.org_id !== a!.org_id) return;
            this._resolvedContextDocs.set(resp.context_documents);
            this._isLoadingContextDocs.set(false);
          },
          error: (err: unknown) => {
            this._contextDocsError.set(
              getUserFriendlyErrorMessage(
                err,
                this.transloco.translate<string>('panel_assessment_info.context_docs_load_error'),
                this.transloco
              )
            );
            this._isLoadingContextDocs.set(false);
          },
        });
      onCleanup(() => {
        sub.unsubscribe();
        this._isLoadingContextDocs.set(false);
      });
    });
  }

  onTaskCountChanged(count: number): void {
    this._taskCount.set(count);
  }

  // --- Constants ---
  readonly getAssessmentStatusDisplayName = getAssessmentStatusLabel;
  readonly getAssessmentStatusVariant = getAssessmentStatusVariant;
  readonly getReviewStatusLabel = getReviewStatusLabel;
  readonly getReviewStatusVariant = getReviewStatusVariant;

  // --- Tab switching ---
  setTab(tab: 'overview' | 'risk' | 'tasks' | 'history'): void {
    this.activeTab.set(tab);
  }

  // --- Panel actions ---
  onClose(): void {
    this.panelClose.emit();
  }

  onOpenInNewTab(): void {
    this.openInNewTab.emit();
  }

  onRequestReview(): void {
    const a = this.assessment();
    if (a) this.requestReview.emit(a);
  }

  onApprove(): void {
    const a = this.assessment();
    if (a && this.canApproveDirectly()) this.approve.emit(a);
  }

  onEdit(): void {
    const a = this.assessment();
    if (a) this.edit.emit(a);
  }

  onExport(): void {
    const a = this.assessment();
    if (a) this.export.emit(a);
  }

  onShare(): void {
    const a = this.assessment();
    if (a) this.share.emit(a);
  }

  onDelete(): void {
    const a = this.assessment();
    if (a) this.delete.emit(a);
  }

  onStartFullTia(): void {
    const a = this.assessment();
    if (a) this.startFullTia.emit(a);
  }

  /** Record-only TIA (VER-377) — no editor, report or review to open; the
   *  drawer shows its summary instead. */
  readonly isRecordOnly = computed(() => isRecordOnlyTia(this.assessment()));
  readonly canStartFullTia = computed(() => canReplaceRecordOnlyTia(this.assessment()));
  /** A superseded/expired record can be deleted (it's no longer the open
   *  record for its transfer); an open one offers "Create TIA" instead. */
  readonly isRetiredRecordOnly = computed(() => isRetiredRecordOnlyTia(this.assessment()));

  /** Build the linked-PA URL for the hero chip's `<a [href] target="_blank">`.
   *  The anchor's default click behaviour (with `target="_blank"`)
   *  opens the PA edit modal in a new tab — no click handler needed;
   *  cmd/middle/right-click give the same new-tab result via the
   *  browser natively. Returns null when the assessment has no linked
   *  PA (the chip is not rendered in that case). */
  linkedPaHref(): string | null {
    const activityId = this.assessment()?.pa_link?.activity_id;
    if (!activityId) return null;
    return this.entityLink.activityListUrl(activityId, this.assessment()?.org_id ?? null);
  }

  onJumpToReport(): void {
    this.activeTab.set('risk');
  }

  // --- Assessment helpers ---
  isAssessmentLocked(assessment: AssessmentRecord): boolean {
    if (this.isSuperManager()) return false;
    return (
      assessment.review_status === AssessmentReviewStatus.IN_PROGRESS ||
      assessment.review_status === AssessmentReviewStatus.REVIEWED
    );
  }

  /**
   * May this caller delete *this* assessment?
   *
   * Asked against the record's own `org_id`, not the effective org: this
   * panel opens over the review queue, which spans customers.
   *
   * Legacy answer is `true` — before granular access the item was offered
   * subject only to the review-lock — so an org the backend has not
   * composed access for behaves exactly as it did. Kept in step with the
   * same helper on the two assessment list routes.
   *
   * Scoped to Delete. Export and Share on this menu stay ungated pending a
   * decision on whether they should be.
   */
  canDeleteAssessment(assessment: AssessmentRecord): boolean {
    return this.stateService.holdsCapabilityIn(assessment.org_id, 'assessment_write', true);
  }

  /** Tooltip for the Delete item. The permission answer outranks the
   *  review-lock — waiting for a review to finish will not grant the
   *  capability. Empty when nothing blocks, matching the menu's other
   *  items, which carry no tooltip. */
  deleteDisabledTooltip(assessment: AssessmentRecord): string {
    if (!this.canDeleteAssessment(assessment)) {
      return this.transloco.translate<string>('disabled_tooltips.no_write_capability_assessments');
    }
    return this.isAssessmentLocked(assessment)
      ? this.transloco.translate<string>('assessment.menu_delete_locked_tooltip')
      : '';
  }

  getDisplayOrgName(assessment: AssessmentRecord): string | null {
    return (
      this.displayOrgName() ??
      getAssessmentDisplayOrgName(assessment, this.stateService.organizations())
    );
  }

  readonly getAssessmentIconConfig = getAssessmentIconConfig;
  readonly getInitials = getInitials;
  readonly getAvatarColor = getAvatarColor;

  readonly creatorDisplayName = computed(() => {
    const a = this.assessment();
    return a?.created_by_name ?? null;
  });

  /** Commit a header-driven rename. Fires from `<app-editable-panel-title>`
   *  on blur/Enter when the name actually changed. Super-manager only. */
  async onTitleCommit(newName: string, a: AssessmentRecord): Promise<void> {
    if (!this.isSuperManager()) return;
    try {
      const response = (await firstValueFrom(
        this.orgManagementApi.updateAssessment(a.org_id, a.assessment_id, {
          assessment_name: newName,
        })
      )) as AssessmentResponse;
      if (response.assessment) {
        this.assessmentUpdated.emit(response.assessment);
      }
    } catch (error) {
      console.error('Failed to rename assessment:', error);
    }
  }
}
